<!-- Focusable only by script: where focus goes back to when a payment lock ends and no pay control is ready yet. -->
<div class="cw-adyen" tabindex="-1" role="group" [attr.aria-label]="'adyen.payment_method_label' | translate">
  @if (resuming() && !errorMessage()) {
    <div class="cw-adyen__loading" role="status">
      {{ resumeI18n.CHECKING | translate }}
    </div>
  } @else if (isLoading()) {
    <div class="cw-adyen__loading">
      {{ 'adyen.loading' | translate }}
    </div>
  }

  @if (errorMessage(); as error) {
    <div class="alert alert-danger summary-warning mt-2" role="alert">
      {{ error | translate }}
      @if (canRetry()) {
        <button type="button" class="btn btn-link alert-link p-0 cw-adyen__retry" (click)="retry()">
          {{ 'adyen.retry' | translate }}
        </button>
      }
    </div>
  }

  <!--
    Hosted checkout: this page's origin is not registered on the Adyen client key, so no Drop-in is
    mounted - the shopper pays on Adyen's own page in a popup. Our own markup only, so it can stay in
    the shadow root.
  -->
  @if (hostedSession()) {
    <div class="cw-adyen__hosted">
      <app-button
        id="cw-adyen-hosted-pay"
        customClass="cw-adyen__hosted-pay"
        [isDisabled]="hostedPaid()"
        [fontColor]="widgetService?.widgetConf?.button_font_color"
        [backgroundColor]="widgetService?.widgetConf?.button_background_color"
        [ariaDescribedby]="'cw-adyen-hosted-hint'"
        (clickEvent)="openHostedPayment()"
      >
        {{ hostedI18n.PAY_BUTTON | translate }}
      </app-button>
      <p id="cw-adyen-hosted-hint" class="cw-adyen__hosted-hint">
        {{ hostedI18n.HINT | translate }}
      </p>
      @if (hostedPopupBlocked()) {
        <div class="alert alert-warning summary-warning mt-2 cw-adyen__popup-blocked" role="alert">
          {{ (isIframeEmbed ? hostedI18n.ALLOW_POPUPS : hostedI18n.POPUP_BLOCKED) | translate }}
        </div>
      }
      <!--
        Polling is the source of truth, so the shopper may pay in any tab: this link (re-)opens
        Adyen's page in the same named window - after a blocked popup in the iframe embed, and while
        a payment is awaited (a phone easily loses the payment tab). rel="opener" keeps the return
        page able to post the result back and close itself.
      -->
      @if (!hostedPaid() && (hostedWatching() || (hostedPopupBlocked() && isIframeEmbed))) {
        @if (hostedWatching()) {
          <p class="cw-adyen__hosted-hint cw-adyen__return-hint" role="status">
            {{ hostedI18n.RETURN_TO_TAB | translate }}
          </p>
        }
        <a
          class="cw-adyen__complete-payment"
          [href]="hostedSession()?.url"
          [target]="hostedPopupName"
          rel="opener"
          (click)="onCompletePaymentLinkClick()"
        >
          {{ hostedI18n.COMPLETE_PAYMENT | translate }}
        </a>
      }
    </div>
  }

  <!--
    Says why everything else on the step is disabled. The live region is always rendered, only its
    text comes and goes, so screen readers announce the change; nothing on this page is inert.
  -->
  <p id="cw-adyen-lock-status" class="cw-adyen__lock-status" role="status" aria-live="polite">
    @if (paymentInFlight() && !resuming()) {
      {{ 'adyen.payment_in_progress' | translate }}
    }
  </p>

  <!--
    While a payment is in progress the summary is locked (Back, Book, the payment method, the coupon,
    the cart), so this is the shopper's one way out. Not while a returned payment is being finished.
    A window opened by the "Complete payment" link is one this page cannot close, so the shopper is
    asked to close it instead; the lock then ends on the result or the session's expiry.
  -->
  @if (paymentInFlight() && !resuming()) {
    <div class="cw-adyen__cancel">
      @if (hostedWindowUntracked()) {
        <p class="cw-adyen__hosted-hint cw-adyen__close-window-first">
          {{ hostedI18n.CLOSE_WINDOW_FIRST | translate }}
        </p>
      } @else {
        <app-button
          id="cw-adyen-cancel-payment"
          customClass="cw-adyen__cancel-payment"
          [isDisabled]="!canCancel()"
          [fontColor]="widgetService?.widgetConf?.button_font_color"
          [backgroundColor]="widgetService?.widgetConf?.button_background_color"
          [ariaDescribedby]="'cw-adyen-lock-status'"
          (clickEvent)="cancelPayment()"
        >
          {{ 'adyen.cancel_payment' | translate }}
        </app-button>
      }
    </div>
  }

  <!--
    Drop-in owns everything inside this element, and it has to be reachable from
    `document`. AdyenLoaderService appends adyen.css to document.head, and a
    stylesheet there cannot cross a shadow boundary — inside the web component
    the Drop-in would render completely unstyled, which is silent: it mounts,
    it works, it just looks broken. Projecting the container into the light DOM
    through a slot is the same fix cw-paypal and stripe already carry.

    Only the container crosses; the loading and error states stay in the shadow
    root. Still no @if around it — hiding it behind a condition is how
    "container not found" bugs start. The ViewChild can no longer be static,
    because a structural directive's view does not exist yet when static
    queries resolve; every dereference of it already sits behind an await and
    the null guard in mountDropin().
  -->
  <ng-container *calioLightDomProjection>
    <div #dropinContainer class="cw-adyen__dropin"></div>
  </ng-container>
</div>
